import React, { useEffect, useRef } from 'react';
import { T } from '../../designTokens';

interface ScreenshotLightboxProps {
  src: string;
  url: string;
  roleLabel: string;
  width: number;
  height: number;
  onClose: () => void;
}

/**
 * Full-size view of one proof-of-scan screenshot (epic #1331). DESIGN.md:
 * radius-lg panel on a dark backdrop, the image at natural size capped to
 * the viewport. Closes on Escape, backdrop click, or the close button; the
 * close button is the only focusable control, so Tab stays on it. Never
 * printed — thumbnails print inline instead.
 */
export default function ScreenshotLightbox({ src, url, roleLabel, width, height, onClose }: ScreenshotLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        closeRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      opener?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="no-print"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        backgroundColor: T.scrim,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Screenshot: ${roleLabel}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: T.white,
          borderRadius: 10,
          border: `1px solid ${T.gray200}`,
          maxWidth: '100%',
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            borderBottom: `1px solid ${T.gray200}`,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.gray500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {roleLabel}
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: T.fontMono, fontSize: 13, color: T.gray700, wordBreak: 'break-all' }}
            >
              {url}
            </a>
          </div>
          <span style={{ fontFamily: T.fontMono, fontSize: 12, color: T.gray500, whiteSpace: 'nowrap' }}>
            {width} × {height}
          </span>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close screenshot"
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              border: `1px solid ${T.gray200}`,
              backgroundColor: T.gray50,
              color: T.gray700,
              fontSize: 18,
              lineHeight: 1,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
        <div style={{ overflow: 'auto', backgroundColor: T.gray100 }}>
          <img
            src={src}
            alt={`${roleLabel}: ${url}`}
            width={width}
            height={height}
            style={{
              display: 'block',
              width: 'auto',
              height: 'auto',
              maxWidth: 'calc(100vw - 48px)',
              maxHeight: 'calc(100vh - 120px)',
            }}
          />
        </div>
      </div>
    </div>
  );
}
